import React, { useEffect, useId, useRef, useState } from "react";
import { createIndirizzo, getProfilo } from "../../Admin/adminApi";
import AddressFields, { addressValidationError } from "./AddressFields";
import { supplierAddressCompatibility } from "../../utils/supplierAddressCompatibility";
import {track} from '../../analytics/service';
import "../../pages/css/Profilo.css";
import "./SupplierAddressDialog.css";

// Temporarily hide saved-address selection in the customer dialog.
const showSavedAddressOptions = false;

export default function SupplierAddressDialog({ supplierId, token, options, onClose, onSelect }) {
  const idPrefix = useId();
  const dialog = useRef(null);
  const [edit, setEdit] = useState(null);
  const [addrCityConfirmed, setAddrCityConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    const previous = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialog.current?.focus();
    return () => { document.body.style.overflow = overflow; previous?.focus?.(); };
  }, []);
  const editing = edit != null;
  useEffect(() => { if (editing) dialog.current?.querySelector("input")?.focus(); }, [editing]);
  const select = async address => {
    setError(""); setBusy(true);
    try {
      const result = await supplierAddressCompatibility(address, supplierId, token);
      if (!result.compatible) throw new Error("Questo indirizzo non è nella zona servita dal fornitore.");
      await onSelect(address, result.context || null);
      track('address_flow_result',{outcome:'success'});
    } catch (e) { track('address_flow_result',{outcome:'failure',error_code:'unavailable'});setError(e.message || "Errore selezione indirizzo"); }
    finally { setBusy(false); }
  };
  const start = async () => {
    setBusy(true); setError("");
    try {
      const me = await getProfilo(token);
      setEdit({ label: "", street: "", cap: "", citta: me.citta || "", provincia: me.provincia || "", phone: me.phone || "", notes: "", active: !options.some(a => a.source_type === "user") });
      setAddrCityConfirmed(true);
    } catch { setError("Impossibile caricare il modulo indirizzo. Riprova."); }
    finally { setBusy(false); }
  };
  const save = async event => {
    event.preventDefault();
    const validation = addressValidationError(edit, addrCityConfirmed);
    if (validation) { setError(validation); return; }
    setBusy(true); setError("");
    try {
      // Check the actual supplier before writing, so an incompatible address
      // cannot change the active address or cart context.
      const result = await supplierAddressCompatibility({ ...edit, source_type: "user" }, supplierId, token);
      if (!result.compatible) throw new Error("Questo indirizzo non è nella zona servita dal fornitore.");
      const saved = await createIndirizzo(edit, token);
      await onSelect({ ...saved, source_type: "user", source_id: saved.id }, null);
      track('address_flow_result',{outcome:'success'});
    } catch (e) { track('address_flow_result',{outcome:'failure',error_code:'unavailable'});setError(e.message || "Errore salvataggio indirizzo"); }
    finally { setBusy(false); }
  };
  const keyDown = event => {
    if (event.key === "Escape" && !busy) { event.stopPropagation(); onClose(); }
    if (event.key !== "Tab") return;
    const elements = [...dialog.current.querySelectorAll('button:not(:disabled), input:not(:disabled), select:not(:disabled), [tabindex="0"]')];
    const first = elements[0], last = elements[elements.length - 1];
    if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog.current)) { event.preventDefault(); last?.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
  };
  return <div className="supplier-address-backdrop" onClick={() => !busy && onClose()}>
    <section ref={dialog} className="supplier-address-dialog" role="dialog" aria-modal="true" aria-labelledby={`${idPrefix}-title`} tabIndex={-1} onKeyDown={keyDown} onClick={e => e.stopPropagation()}>
      <header><h2 id={`${idPrefix}-title`}>Aggiungi un indirizzo per continuare</h2><button type="button" className="prf-btn" onClick={onClose} disabled={busy} aria-label="Chiudi">×</button></header>
      <p>Puoi consultare liberamente i servizi e i prezzi di questo fornitore. Per aggiungere articoli al carrello, però, devi indicare un indirizzo nella zona servita.</p>
      {error && <p className="supplier-address-error" role="alert">{error}</p>}
      {edit ? <form className="prf-grid prf-grid--addr" onSubmit={save}>
        <fieldset disabled={busy} className="supplier-address-fields">
          <AddressFields edit={edit} setEdit={setEdit} addrCityConfirmed={addrCityConfirmed} setAddrCityConfirmed={setAddrCityConfirmed} idPrefix={idPrefix} />
        </fieldset>
        <div className="prf-actions"><button className="prf-btn" type="button" disabled={busy} onClick={() => { setEdit(null); setError(""); }}>Annulla</button><button className="prf-btn prf-btn--primary" disabled={busy} type="submit">{busy ? "Salvataggio…" : "Salva"}</button></div>
      </form> : <>
        {showSavedAddressOptions && options.length > 0 && <div className="supplier-address-options"><p>Puoi anche selezionare un indirizzo salvato. Verificheremo che sia servito da questo fornitore.</p>{options.map(a => <button type="button" className="prf-btn" disabled={busy} key={`${a.source_type}:${a.source_id}`} onClick={() => select(a)}>{a.full_address || [a.street, a.citta].filter(Boolean).join(", ") || a.display_name}</button>)}</div>}
        <button type="button" className="prf-btn prf-btn--primary" onClick={start} disabled={busy}>Aggiungi indirizzo</button>
      </>}
    </section>
  </div>;
}
